import React, { useState, useEffect } from 'react';
import { MapPin, List, MapIcon, TrendingUp, TrendingDown, Info, ChevronRight } from 'lucide-react';
import { getRegionsData, getStoresData } from '../../data/mockData';

// Saudi Arabia Region Map component
const SaudiMap = ({ regionsData, onRegionSelect }) => {
  const [hoveredRegion, setHoveredRegion] = useState(null);
  
  // Get color based on score
  const getRegionColor = (score) => {
    if (score >= 85) return '#10b981'; // emerald-500
    if (score >= 75) return '#f59e0b'; // amber-500
    if (score >= 65) return '#ef4444'; // red-500
    return '#6b7280'; // gray-500
  };
  
  // Get color for hover state
  const getHoverColor = (score) => {
    if (score >= 85) return '#059669'; // emerald-600
    if (score >= 75) return '#d97706'; // amber-600
    if (score >= 65) return '#dc2626'; // red-600
    return '#4b5563'; // gray-600
  };

  return (
    <div className="relative">
      <svg 
        viewBox="0 0 800 600" 
        className="w-full h-auto"
      >
        {/* Western Region */}
        <path
          d="M300,150 L250,200 L230,280 L200,350 L190,420 L210,480 L240,520 L270,480 L290,400 L310,350 L330,300 L320,250 L300,150Z"
          fill={getRegionColor(regionsData.find(r => r.id === 'western').score)}
          stroke={hoveredRegion === 'western' ? getHoverColor(regionsData.find(r => r.id === 'western').score) : '#fff'}
          strokeWidth={hoveredRegion === 'western' ? 3 : 1}
          onMouseEnter={() => setHoveredRegion('western')}
          onMouseLeave={() => setHoveredRegion(null)}
          onClick={() => onRegionSelect('western')}
          className="cursor-pointer transition-colors duration-200"
        />
        
        {/* Central Region */}
        <path
          d="M330,150 L400,150 L450,200 L470,280 L460,350 L430,400 L390,430 L330,390 L310,350 L330,300 L320,250 L330,150Z"
          fill={getRegionColor(regionsData.find(r => r.id === 'central').score)}
          stroke={hoveredRegion === 'central' ? getHoverColor(regionsData.find(r => r.id === 'central').score) : '#fff'}
          strokeWidth={hoveredRegion === 'central' ? 3 : 1}
          onMouseEnter={() => setHoveredRegion('central')}
          onMouseLeave={() => setHoveredRegion(null)}
          onClick={() => onRegionSelect('central')}
          className="cursor-pointer transition-colors duration-200"
        />
        
        {/* Eastern Region */}
        <path
          d="M450,200 L500,180 L550,200 L580,250 L590,300 L570,350 L540,380 L500,400 L470,420 L430,400 L460,350 L470,280 L450,200Z"
          fill={getRegionColor(regionsData.find(r => r.id === 'eastern').score)}
          stroke={hoveredRegion === 'eastern' ? getHoverColor(regionsData.find(r => r.id === 'eastern').score) : '#fff'}
          strokeWidth={hoveredRegion === 'eastern' ? 3 : 1}
          onMouseEnter={() => setHoveredRegion('eastern')}
          onMouseLeave={() => setHoveredRegion(null)}
          onClick={() => onRegionSelect('eastern')}
          className="cursor-pointer transition-colors duration-200"
        />
        
        {/* Northern Region */}
        <path
          d="M300,150 L330,150 L400,150 L450,200 L500,180 L520,150 L530,100 L510,70 L450,50 L380,50 L330,70 L300,100 L300,150Z"
          fill={getRegionColor(regionsData.find(r => r.id === 'northern').score)}
          stroke={hoveredRegion === 'northern' ? getHoverColor(regionsData.find(r => r.id === 'northern').score) : '#fff'}
          strokeWidth={hoveredRegion === 'northern' ? 3 : 1}
          onMouseEnter={() => setHoveredRegion('northern')}
          onMouseLeave={() => setHoveredRegion(null)}
          onClick={() => onRegionSelect('northern')}
          className="cursor-pointer transition-colors duration-200"
        />
        
        {/* Southern Region */}
        <path
          d="M240,520 L270,480 L290,400 L310,350 L330,390 L390,430 L430,400 L470,420 L450,470 L420,510 L380,530 L340,540 L300,530 L270,520 L240,520Z"
          fill={getRegionColor(regionsData.find(r => r.id === 'southern').score)}
          stroke={hoveredRegion === 'southern' ? getHoverColor(regionsData.find(r => r.id === 'southern').score) : '#fff'}
          strokeWidth={hoveredRegion === 'southern' ? 3 : 1}
          onMouseEnter={() => setHoveredRegion('southern')}
          onMouseLeave={() => setHoveredRegion(null)}
          onClick={() => onRegionSelect('southern')}
          className="cursor-pointer transition-colors duration-200"
        />
        
        {/* Region Labels */}
        <text x="350" y="120" className="font-medium text-sm" fill="#1e293b">Northern Region</text>
        <text x="380" y="280" className="font-medium text-sm" fill="#1e293b">Central Region</text>
        <text x="520" y="280" className="font-medium text-sm" fill="#1e293b">Eastern Region</text>
        <text x="260" y="300" className="font-medium text-sm" fill="#1e293b">Western Region</text>
        <text x="350" y="500" className="font-medium text-sm" fill="#1e293b">Southern Region</text>
        
        {/* Major Cities */}
        <circle cx="380" cy="270" r="6" fill="#3b82f6" />
        <text x="390" y="275" className="text-xs" fill="#1e293b">Riyadh</text>
        
        <circle cx="260" cy="250" r="6" fill="#3b82f6" />
        <text x="270" y="255" className="text-xs" fill="#1e293b">Makkah</text>
        
        <circle cx="240" cy="220" r="6" fill="#3b82f6" />
        <text x="250" y="225" className="text-xs" fill="#1e293b">Jeddah</text>
        
        <circle cx="520" cy="250" r="6" fill="#3b82f6" />
        <text x="530" y="255" className="text-xs" fill="#1e293b">Dammam</text>
        
        <circle cx="350" y="510" r="6" fill="#3b82f6" />
        <text x="360" y="515" className="text-xs" fill="#1e293b">Abha</text>
        
        <circle cx="400" cy="110" r="6" fill="#3b82f6" />
        <text x="410" y="115" className="text-xs" fill="#1e293b">Tabuk</text>
      </svg>
      
      {/* Map Legend */}
      <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-md border border-gray-200">
        <div className="text-xs font-semibold mb-2">Score Legend</div>
        <div className="flex items-center mb-1">
          <div className="w-4 h-4 bg-emerald-500 rounded-sm mr-2"></div>
          <span className="text-xs">85+ (Excellent)</span>
        </div>
        <div className="flex items-center mb-1">
          <div className="w-4 h-4 bg-amber-500 rounded-sm mr-2"></div>
          <span className="text-xs">75-84 (Good)</span>
        </div>
        <div className="flex items-center mb-1">
          <div className="w-4 h-4 bg-red-500 rounded-sm mr-2"></div>
          <span className="text-xs">65-74 (Needs Improvement)</span>
        </div>
        <div className="flex items-center">
          <div className="w-4 h-4 bg-gray-500 rounded-sm mr-2"></div>
          <span className="text-xs">&lt;65 (Critical)</span>
        </div>
      </div>
      
      {/* Hover Information */}
      {hoveredRegion && (
        <div className="absolute top-4 left-4 bg-white p-3 rounded-lg shadow-md border border-gray-200 min-w-[200px]">
          <div className="font-medium mb-1">
            {regionsData.find(r => r.id === hoveredRegion).name}
          </div>
          <div className="text-xs text-gray-600 mb-1">
            {regionsData.find(r => r.id === hoveredRegion).storeCount} Stores
          </div>
          <div className="flex justify-between items-center">
            <div className="text-sm font-semibold">
              Score: {regionsData.find(r => r.id === hoveredRegion).score}
            </div>
            <div className={`text-xs flex items-center ${
              regionsData.find(r => r.id === hoveredRegion).change > 0 
                ? 'text-green-600' 
                : 'text-red-600'
            }`}>
              {regionsData.find(r => r.id === hoveredRegion).change > 0 ? (
                <TrendingUp className="h-3 w-3 mr-1" />
              ) : (
                <TrendingDown className="h-3 w-3 mr-1" />
              )}
              {Math.abs(regionsData.find(r => r.id === hoveredRegion).change)}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Regional Store List component
const RegionStoreList = ({ region, stores }) => {
  const [showAll, setShowAll] = useState(false);
  
  // Helper to get color based on score
  const getScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-rose-600';
  };

  // Helper to get background color for score
  const getScoreBgColor = (score) => {
    if (score >= 85) return 'bg-emerald-100';
    if (score >= 70) return 'bg-amber-100';
    return 'bg-rose-100';
  };
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex justify-between items-center">
        <div className="flex items-center">
          <div className="p-2 bg-blue-100 rounded-lg">
            <MapPin className="h-5 w-5 text-blue-600" />
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900">{region.name}</h3>
            <p className="text-sm text-gray-500">{region.storeCount} Stores</p>
          </div>
        </div>
        <div className="flex flex-col items-end">
          <div className="text-xl font-bold text-gray-900">{region.score}</div>
          <div className={`flex items-center text-sm ${
            region.change > 0 ? 'text-emerald-600' : 'text-rose-600'
          }`}>
            {region.change > 0 ? (
              <TrendingUp className="h-4 w-4 mr-1" />
            ) : (
              <TrendingDown className="h-4 w-4 mr-1" />
            )}
            {Math.abs(region.change)}%
          </div>
        </div>
      </div>
      
      <div className="divide-y divide-gray-100">
        {stores
          .filter(s => s.region.toLowerCase() === region.id)
          .sort((a, b) => b.totalScore - a.totalScore)
          .slice(0, showAll ? undefined : 5)
          .map((store, index) => (
            <div key={store.id} className="flex items-center p-3 hover:bg-gray-50 transition-colors">
              <div className="flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center font-medium text-sm bg-gray-100 text-gray-600">
                {index + 1}
              </div>
              <div className="ml-3 flex-1">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">{store.name}</p>
                  <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${getScoreBgColor(store.totalScore)} ${getScoreColor(store.totalScore)}`}>
                    {store.totalScore}
                  </div>
                </div>
                <div className="flex justify-between">
                  <p className="text-xs text-gray-500">{store.city}</p>
                  <p className="text-xs text-gray-500">{store.managerName}</p>
                </div>
              </div>
            </div>
          ))}
      </div>
      
      {stores.filter(s => s.region.toLowerCase() === region.id).length > 5 && (
        <div className="p-3 border-t border-gray-100">
          <button 
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
          >
            {showAll ? 'Show Less' : 'Show All Stores'}
            <ChevronRight className="ml-1 h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// Statistics Card component
const StatisticsCard = ({ title, value, change, icon: Icon }) => {
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-all">
      <div className="flex items-center justify-between">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Icon className="h-5 w-5 text-blue-600" />
        </div>
        <div className={`flex items-center text-sm ${
          change > 0 ? 'text-emerald-600' : 'text-rose-600'
        }`}>
          {change > 0 ? (
            <TrendingUp className="h-4 w-4 mr-1" />
          ) : (
            <TrendingDown className="h-4 w-4 mr-1" />
          )}
          {Math.abs(change)}%
        </div>
      </div>
      <div className="mt-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
    </div>
  );
};

const RegionMap = () => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionsData, setRegionsData] = useState([]);
  const [storesData, setStoresData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      // Load regions and stores data
      const regions = await getRegionsData();
      const stores = await getStoresData();
      
      setRegionsData(regions);
      setStoresData(stores);
      setIsLoading(false);
    };
    
    loadData();
  }, []);
  
  const handleRegionSelect = (regionId) => {
    setSelectedRegion(regionId);
  };
  
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Regional Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatisticsCard 
          title="Total Stores" 
          value="104" 
          change={3.2} 
          icon={MapPin} 
        />
        <StatisticsCard 
          title="Avg Store Score" 
          value="78.5" 
          change={1.8} 
          icon={List} 
        />
        <StatisticsCard 
          title="Highest Region" 
          value="Central (89.4)" 
          change={4.1} 
          icon={TrendingUp} 
        />
        <StatisticsCard 
          title="Stores Below Target" 
          value="22" 
          change={-5.2} 
          icon={Info} 
        />
      </div>
      
      {/* Map & Region Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* SVG Map */}
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <MapIcon className="h-5 w-5 mr-2 text-blue-600" />
            Performance Map
          </h2>
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
            <SaudiMap regionsData={regionsData} onRegionSelect={handleRegionSelect} />
          </div>
          <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2 flex-shrink-0" />
              <p className="text-sm text-blue-800">
                Click on a region to view detailed store performance. Hover over regions to see summary statistics. 
                This map visualizes the overall performance score for each region based on the average of all stores in that area.
              </p>
            </div>
          </div>
        </div>
        
        {/* Region Stores List */}
        <div className="lg:col-span-1">
          {selectedRegion ? (
            <RegionStoreList 
              region={regionsData.find(r => r.id === selectedRegion)} 
              stores={storesData}
            />
          ) : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full flex flex-col items-center justify-center">
              <MapPin className="h-12 w-12 text-gray-300 mb-4" />
              <p className="text-gray-500 text-center">
                Select a region on the map to view store details
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* All Regions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">All Regions Overview</h2>
          <p className="text-sm text-gray-500 mt-1">Performance metrics by region</p>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Region
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stores
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Avg Score
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sales
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reviews
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Maintenance
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Service
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Top Store
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {regionsData.map((region) => (
                <tr key={region.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{region.name}</div>
                        <div className="text-xs text-gray-500">{region.id.charAt(0).toUpperCase() + region.id.slice(1)}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {region.storeCount}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium inline-flex items-center ${
                      region.score >= 85 ? 'bg-emerald-100 text-emerald-800' :
                      region.score >= 75 ? 'bg-amber-100 text-amber-800' :
                      'bg-rose-100 text-rose-800'
                    }`}>
                      {region.score}
                      <span className={`ml-1 ${
                        region.change > 0 ? 'text-emerald-600' : 'text-rose-600'
                      }`}>
                        {region.change > 0 ? '↑' : '↓'}{Math.abs(region.change)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {region.salesScore}/30
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {region.reviewsScore}/20
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {region.maintenanceScore}/10
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {region.serviceScore}/15
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {region.topStore}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RegionMap; 